import { RiPencilLine, RiRefreshLine } from '@remixicon/react';
import { type ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import type { RouteComponentProps, RouteElement } from '@shared/router';
import Link from '@shared/router/link';
import * as history from '@shared/router/location';
import RouteNotFound from '@shared/views/bootstrap/RouteNotFound';

import { Badge } from '@playbook/atoms/badge';
import { Button } from '@playbook/atoms/button';
import { Card } from '@playbook/atoms/card';

import { Action, type Key } from '@portal/pkg/api/types';
import * as routes from '@portal/pkg/routes';
import { CodeBlock, CopyableValue } from '@portal/views/_shared/code-block';
import { PageHeader, PageSection } from '@portal/views/_shared/page';
import { PageLoader } from '@portal/views/_shared/page-loader';
import { KeyPageFrame, useKey } from '@portal/views/keys/KeyPageFrame';
import { LoadFailedInline } from '@portal/views/keys/LoadFailed';
import {
	KeyStatusBadge,
	can,
	formatDate,
	formatDateTime,
	generations,
} from '@portal/views/keys/_shared';
import { KeyActivity } from '@portal/views/keys/detail/KeyActivity';

import css from './styles.module.css';

export default function KeyPage({ params }: RouteComponentProps): RouteElement {
	return (
		<KeyPageFrame width="wide">
			<KeyDetail id={params?.['key']?.toString()} />
		</KeyPageFrame>
	);
}

function KeyDetail({ id }: { id: Optional<string> }): ReactNode {
	const key = useKey(id);

	if (!key) {
		return <RouteNotFound />;
	}

	return (
		<div className={css.detail}>
			<PageHeader
				title={key.name}
				description={describe(key)}
				badge={<KeyStatusBadge forKey={key} />}
				breadcrumb={
					<Link href={routes.Keys()}>
						<span className={css.breadcrumb}>← API keys</span>
					</Link>
				}
				actions={
					can(key, Action.RotateKey) && (
						<Button
							variant="primary"
							label="Rotate"
							leadingIcon={<RiRefreshLine />}
							onClick={() => history.push(routes.KeyRotate(key.id))}
						/>
					)
				}
			/>

			<PageSection>
				<CopyableValue
					name="keyId"
					value={key.id}
					label="Key ID"
					help="The identity your server signs as — it goes in the iss and sub claims of the assertion."
				/>
			</PageSection>

			<PageSection title="Access">
				<div className={css.access}>
					<ul className={css.capabilities}>
						{key.capabilities.map((capability) => (
							<li key={capability}>
								<Badge label={capability} tint="200" />
							</li>
						))}
					</ul>
					{can(key, Action.UpdateKey) && (
						<Button
							size="small"
							label="Edit"
							leadingIcon={<RiPencilLine />}
							onClick={() => history.push(routes.KeyAccess(key.id))}
						/>
					)}
				</div>
			</PageSection>

			<PageSection title="Public key">
				<CodeBlock name="publicKey" value={key.publicKey} rows={5} />
			</PageSection>

			<div className={css.columns}>
				<PageSection
					title="Key history"
					description="Every generation this key has had. Each is a separate key with its own id and its own activity.">
					<KeyHistory forKey={key} />
				</PageSection>

				<PageSection
					title="Activity"
					description="Everything that has happened to this generation. Using the key is not recorded — only changes are.">
					<ErrorBoundary
						fallback={
							<LoadFailedInline what="Could not load this key's activity." />
						}>
						<Suspense fallback={<PageLoader />}>
							<KeyActivity id={key.id} />
						</Suspense>
					</ErrorBoundary>
				</PageSection>
			</div>

			{can(key, Action.RevokeKey) && <RevokeSection forKey={key} />}
		</div>
	);
}

function describe(key: Key): string {
	const parts = [`Created ${formatDate(key.createdAt)}`];

	if (key.lastUsedAt) {
		parts.push(`last used ${formatDate(key.lastUsedAt)}`);
	} else {
		parts.push('never used');
	}

	if (key.revokedAt) {
		parts.push(`revoked ${formatDate(key.revokedAt)}`);
	} else {
		parts.push(`expires ${formatDateTime(key.expiresAt)}`);
	}

	return parts.join(' · ');
}

function KeyHistory({ forKey }: { forKey: Key }): ReactNode {
	const chain = generations(forKey);

	if (chain.length === 1) {
		return <p className={css.quiet}>This key has never been rotated.</p>;
	}

	return (
		<ol className={css.history}>
			{chain.map((generation, index) => (
				<li key={generation.id}>
					<div className={css.generationTitle}>
						{index === 0 ? (
							<span className={css.generationName}>This key</span>
						) : (
							<Link
								className={css.generationName}
								href={routes.Key(generation.id)}>
								{ordinal(chain.length - index)} generation
							</Link>
						)}
						<KeyStatusBadge forKey={generation} />
					</div>
					<code className={css.generationId}>{generation.id}</code>
					<p className={css.quiet}>
						{formatDate(generation.createdAt)} →{' '}
						{generation.revokedAt
							? `revoked ${formatDate(generation.revokedAt)}`
							: formatDateTime(generation.expiresAt)}
					</p>
				</li>
			))}
		</ol>
	);
}

function RevokeSection({ forKey }: { forKey: Key }): ReactNode {
	return (
		<Card>
			<div className={css.revoke}>
				<div>
					<h3>Revoke this key</h3>
					<p className={css.quiet}>
						It stops working immediately and cannot be brought back. Use this if
						the private key has leaked.
					</p>
				</div>
				<Button
					variant="destructive"
					label="Revoke…"
					onClick={() => history.push(routes.KeyRevoke(forKey.id))}
				/>
			</div>
		</Card>
	);
}

const Ordinals = ['First', 'Second', 'Third', 'Fourth', 'Fifth'];

function ordinal(position: number): string {
	return Ordinals[position - 1] ?? `${position}th`;
}
